import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import CloseIcon from '@publicImages/svg/quick-find-coaching-close.svg?static';
import TrashIcon from '@publicImages/svg/trash.svg?static';

import HtmlContent from '@/components/elements/HtmlContent';
import Tooltip from '@/components/Tooltip';
import UIResource from '@/components/UIResource';
import { resetFilters } from '@/ducks/filters/setters';
import { type FiltersKey } from '@/ducks/filters/types';
import { getQFSearchParams } from '@/features/quickFindFilters/helpers';
import { useQFActiveLink } from '@/features/quickFindFilters/hooks';
import ClientOnlyPortal from '@/helpers/clientOnlyPortal';
import { getSessionStorageValue, setSessionStorageValue } from '@/helpers/util';

import styles from './QuickFindFilterChip.module.scss';

type CoachingTooltipProps = {
  closeHandler: () => void;
  isOpen: boolean;
  parentRef: React.RefObject<HTMLDivElement>;
};
const CoachingTooltip = ({ closeHandler, isOpen, parentRef }: CoachingTooltipProps) => {
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setPosition({
        left: rect.left + rect.width / 2,
        top: rect.top + rect.height,
      });
    }
  }, [isOpen, parentRef]);

  if (!isOpen) {
    return null;
  }

  return (
    <ClientOnlyPortal id="root">
      <div className={styles.coachingTooltip} style={{ left: position.left, top: position.top }}>
        <span className={styles.coachingTooltipText}>
          <UIResource id="QuickFind.tooltip" />
        </span>

        <div className={styles.coachingTooltipClose} onClick={closeHandler}>
          <CloseIcon className={styles.coachingTooltipCloseIcon} />
          <div className="sr-only">Close</div>
        </div>
      </div>
    </ClientOnlyPortal>
  );
};

const QuickFindFilterChip = () => {
  const [isCoachingMode, setIsCoachingMode] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeQFLink = useQFActiveLink();

  useEffect(() => {
    if (activeQFLink && !getSessionStorageValue('isQuickFindCoachingUsed')) {
      setSessionStorageValue('isQuickFindCoachingUsed', true);
      setIsCoachingMode(true);
    }
  }, [activeQFLink]);

  if (!activeQFLink) {
    return null;
  }

  const onClearHandler = () => {
    if (activeQFLink) {
      const linkParams = getQFSearchParams(activeQFLink);
      const keys = [...linkParams.keys()];
      resetFilters(keys as FiltersKey[]);
    }
  };

  return (
    <div className={cn(styles.root, { [styles.coaching as string]: isCoachingMode })} ref={ref}>
      <div className={styles.wrapper}>
        {activeQFLink.description ? (
          <Tooltip
            opener={activeQFLink.linkTitle}
            openerClassName={styles.text}
            popupClassName={styles.tooltipContent}
            portal
            side="bottom"
          >
            <HtmlContent content={activeQFLink.description} />
          </Tooltip>
        ) : (
          <span className={styles.text}>{activeQFLink.linkTitle}</span>
        )}

        <button aria-label="Remove quick find filter" className={styles.button} onClick={onClearHandler}>
          <TrashIcon className={styles.icon} />
          <UIResource id="QuickFind.clear.CTA" />
        </button>
      </div>

      <CoachingTooltip
        closeHandler={() => {
          setIsCoachingMode(false);
        }}
        isOpen={isCoachingMode}
        parentRef={ref}
      />
    </div>
  );
};

export default QuickFindFilterChip;
